import type { AuditEntityType, AuditLogEntry } from '@hemmafixa/shared';
import {
  DataTable,
  EmptyState,
  FilterBar,
  FilterChips,
  PageHeader,
  Pagination,
  type ColumnDef,
} from '@hemmafixa/ui';
import { ScrollText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { QueryView } from '../shared/query-view';
import { useAuditLog } from './api';
import { auditChanges, auditTarget, useAuditAction } from './audit-format';

type Filter = 'all' | AuditEntityType;
const PAGE_SIZE = 25;

/** Every audited change, newest first (P2-10). */
export function AuditPage() {
  const { t, dateTime } = useI18n();
  const actionLabel = useAuditAction();
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const a = t.admin.audit;

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useAuditLog({
    entityType: filter === 'all' ? undefined : filter,
    q: searchQuery || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const columns = useMemo<ColumnDef<AuditLogEntry, unknown>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: a.columns.when,
        enableSorting: false,
        cell: ({ row }) => <span className="whitespace-nowrap">{dateTime(row.original.createdAt)}</span>,
      },
      {
        id: 'actor',
        header: a.columns.who,
        cell: ({ row }) =>
          row.original.actor ? (
            <Link to={`/admin/users/${row.original.actor.id}`} className="underline underline-offset-2">
              {row.original.actor.name}
            </Link>
          ) : (
            a.system
          ),
      },
      {
        accessorKey: 'action',
        header: a.columns.what,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="grid gap-0.5">
            <span className="font-medium">{actionLabel(row.original.action)}</span>
            {auditChanges(row.original).map((line) => (
              <span key={line} className="break-words text-xs text-muted-foreground">
                {line}
              </span>
            ))}
          </div>
        ),
      },
      {
        id: 'target',
        header: () => <span className="sr-only">{a.open}</span>,
        cell: ({ row }) => {
          const target = auditTarget(row.original);
          return (
            target && (
              <Link to={target} className="text-sm font-medium underline underline-offset-2">
                {a.open}
              </Link>
            )
          );
        },
      },
    ],
    [a, dateTime, actionLabel],
  );

  return (
    <div className="grid gap-4">
      <PageHeader title={a.title} description={a.description} />
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder={a.searchPlaceholder}
        summary={
          query.data
            ? fill(t.common.showingCount, { shown: query.data.items.length, total: query.data.total })
            : undefined
        }
        hasActiveFilters={search !== '' || filter !== 'all'}
        onClear={() => {
          setSearch('');
          setFilter('all');
          setPage(1);
        }}
        clearLabel={t.common.clearFilters}
        chips={
          <FilterChips
            label={a.title}
            value={filter}
            onChange={(value) => {
              setFilter(value);
              setPage(1);
            }}
            options={(['all', 'user', 'provider', 'job', 'report'] as const).map((value) => ({
              value,
              label: a.filters[value],
            }))}
          />
        }
      />
      <QueryView query={query}>
        {(result) => (
          <>
            <DataTable
              columns={columns}
              data={result.items}
              getRowId={(entry) => entry.id}
              renderCard={(entry) => {
                const target = auditTarget(entry);
                return (
                  <div className="grid gap-1 rounded-xl border border-border bg-surface p-4 text-sm">
                    <p className="font-semibold">{actionLabel(entry.action)}</p>
                    <p className="text-muted-foreground">
                      {entry.actor?.name ?? a.system} · {dateTime(entry.createdAt)}
                    </p>
                    {auditChanges(entry).map((line) => (
                      <p key={line} className="break-words text-muted-foreground">
                        {line}
                      </p>
                    ))}
                    {target && (
                      <Link
                        to={target}
                        className="justify-self-start font-medium underline underline-offset-2"
                      >
                        {a.open}
                      </Link>
                    )}
                  </div>
                );
              }}
              empty={
                search !== '' || filter !== 'all' ? (
                  <EmptyState
                    icon={ScrollText}
                    title={t.common.noMatchesTitle}
                    description={t.common.noMatchesText}
                  />
                ) : (
                  <EmptyState icon={ScrollText} title={a.empty} />
                )
              }
            />
            <Pagination
              page={result.page}
              pageSize={result.pageSize}
              total={result.total}
              onPageChange={setPage}
              labels={{
                previous: t.common.previous,
                next: t.common.next,
                showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
              }}
            />
          </>
        )}
      </QueryView>
    </div>
  );
}
